import { BaseAPI } from "./BaseAPI";

import type { APIConf } from "./BaseAPI";
import type {
  CreateEncodingProfileResponse,
  CreateLiveParams,
  CreateReplayParams,
  CreateReplayResponse,
  CreateS3UploadPayload,
  CreateS3UploadResponse,
  DeleteSourceMediaParams,
  DeleteSourceMediaResponse,
  EncodingProfilePayload,
  EncodingStatus,
  EncodingType,
  GetEncodingListParams,
  GetEncodingListResponse,
  GetEncodingPlanResponse,
  GetEncodingProfileListResponse,
  GetEncodingProfileResponse,
  GetLiveDetailsResponse,
  GetLivesStatsResponse,
  GetLiveStatsResponse,
  GetMediaEncodingStatusResponse,
  GetQualitiesCostResponse,
  GetSourceResponse,
  GetStreamsResponse,
  SetEncodingProfileDefaultResponse,
  StartEncodingParams,
  StartPivotEncodingResponse,
  UpdateEncodingProfileParams,
  UpdateEncodingProfileResponse,
  UpdateLiveParams,
  UpdateLiveResponse,
  UpdateReplayParams,
  UpdateReplayResponse,
  UpdateStreamData,
  UploadLinkPayload,
  UploadLinkResponse,
  UploadStartPayload,
  UploadStartResponse,
  UploadVttPayload,
  UploadVttResponse,
} from "./EncoderAPI.types";

class EncoderAPIClass extends BaseAPI {
  constructor(conf: APIConf) {
    super(conf);
    this.service = "encoder";
    this.version = "v1";
  }

  async createReplay({
    mediaId,
    replayTimeStart,
    replayTimeEnd,
  }: CreateReplayParams): Promise<CreateReplayResponse> {
    return this.axios
      .post<CreateReplayResponse>(`${this.routeBase}/admin/create_replay`, {
        media_uuid: mediaId,
        tc_in: replayTimeStart,
        tc_out: replayTimeEnd,
      })
      .then((response) => response.data);
  }

  async updateReplay({
    replayId,
    replayTimeStart,
    replayTimeEnd,
  }: UpdateReplayParams): Promise<UpdateReplayResponse> {
    return this.axios
      .post<UpdateReplayResponse>(
        `${this.routeBase}/admin/edit_replay/${replayId}`,
        {
          tc_in: replayTimeStart,
          tc_out: replayTimeEnd,
        },
      )
      .then((response) => response.data);
  }

  async getLiveStats(mediaId: string): Promise<GetLiveStatsResponse> {
    return this.axios
      .get<GetLiveStatsResponse>(
        `${this.routeBase}/admin/live/${mediaId}/stats`,
      )
      .then((response) => response.data);
  }

  async getLivesStats(): Promise<GetLivesStatsResponse> {
    return this.axios
      .get<GetLivesStatsResponse>(`${this.routeBase}/admin/lives/stats`)
      .then((response) => response.data);
  }

  async getLiveDetails(mediaId: string): Promise<GetLiveDetailsResponse> {
    return this.axios
      .get<GetLiveDetailsResponse>(`${this.routeBase}/admin/live/${mediaId}`)
      .then((response) => response.data);
  }

  async createLive({
    mediaId,
    maxResolution,
    inputType,
  }: CreateLiveParams): Promise<unknown> {
    return this.axios
      .post(`${this.routeBase}/admin/create_live`, {
        media_uuid: mediaId,
        max_resolution: maxResolution,
        input_type: inputType,
      })
      .then((response) => response.data);
  }

  async publishLive(mediaId: string): Promise<unknown> {
    return this.axios
      .post(`${this.routeBase}/admin/live/${mediaId}/publish_live`)
      .then((response) => response.data);
  }

  async updateLive({
    mediaId,
    time,
  }: UpdateLiveParams): Promise<UpdateLiveResponse> {
    return this.axios
      .post<UpdateLiveResponse>(
        `${this.routeBase}/admin/live/${mediaId}/add_time`,
        {
          time,
        },
      )
      .then((response) => response.data);
  }

  async stopLive(mediaId: string): Promise<unknown> {
    if (!mediaId)
      return Promise.reject(
        new Error("mediaId parameter is required to stop live"),
      );
    return this.axios
      .post(`${this.routeBase}/admin/live/${mediaId}/stop`)
      .then((response) => response.data);
  }

  async deleteFastAsset(fastAssetId: string): Promise<unknown> {
    return this.axios
      .delete(`${this.routeBase}/admin/fast/fast_assets/${fastAssetId}`)
      .then((response) => response.data);
  }

  async startEncoding({
    mediaId,
    profileUuid,
  }: StartEncodingParams): Promise<unknown> {
    return this.axios
      .post(`${this.routeBase}/admin/medias/${mediaId}/encode`, {
        profile_uuid: profileUuid,
      })
      .then((response) => response.data);
  }

  async uploadLink(data: UploadLinkPayload): Promise<UploadLinkResponse> {
    return this.axios
      .post<UploadLinkResponse>(
        `${this.apiBase}/${this.service}/v2/admin/source_medias/upload/link`,
        data,
      )
      .then((response) => response.data);
  }

  async getMediaEncodingStatus(
    mediaId: string,
  ): Promise<GetMediaEncodingStatusResponse> {
    return this.axios
      .get<GetMediaEncodingStatusResponse>(
        `${this.apiBase}/${this.service}/v2/admin/source_medias/encoding_jobs/${mediaId}/status`,
      )
      .then((response) => response.data);
  }

  async getEncodingList({
    perPage = 10,
    page = 1,
    filters,
    sort,
  }: GetEncodingListParams): Promise<GetEncodingListResponse> {
    const params: {
      page: number;
      per_page: number;
      status?: EncodingStatus;
      type?: EncodingType;
      order_by?: string;
      direction?: "up" | "down";
    } = {
      page,
      per_page: perPage,
    };

    if (filters?.status) {
      params.status = filters.status;
    }

    if (filters?.type) {
      params.type = filters.type;
    }

    if (sort) {
      params.order_by = sort.field;
      params.direction = sort.direction;
    }

    return this.axios
      .get<GetEncodingListResponse>(
        `${this.apiBase}/${this.service}/v2/admin/source_medias/encoding_jobs`,
        {
          params,
        },
      )
      .then((response) => response.data);
  }

  async getStreams(mediaId: string): Promise<GetStreamsResponse> {
    return this.axios
      .get<GetStreamsResponse>(
        `${this.apiBase}/${this.service}/v2/admin/hosted_streams/${mediaId}`,
      )
      .then((response) => response.data);
  }

  async getSource(mediaId: string): Promise<GetSourceResponse> {
    return this.axios
      .get<GetSourceResponse>(
        `${this.apiBase}/${this.service}/v2/admin/source_medias/${mediaId}`,
      )
      .then((response) => response.data);
  }

  async deleteSourceMedia({
    sourceMediaId,
    mediaId,
    filename,
  }: DeleteSourceMediaParams): Promise<DeleteSourceMediaResponse> {
    return this.axios
      .delete<DeleteSourceMediaResponse>(
        `${this.apiBase}/${this.service}/v2/admin/source_medias/${sourceMediaId}`,
        {
          data: {
            media_uuid: mediaId,
            filename,
          },
        },
      )
      .then((response) => response.data);
  }

  async startPivotEncoding({
    mediaId,
    profileUuid,
  }: StartEncodingParams): Promise<StartPivotEncodingResponse> {
    return this.axios
      .post<StartPivotEncodingResponse>(
        `${this.apiBase}/${this.service}/v2/admin/source_medias/media_pivots/${mediaId}/encode`,
        {
          encoding_profile_uuid: profileUuid,
        },
      )
      .then((response) => response.data);
  }

  async getPivot(mediaId: string): Promise<GetSourceResponse> {
    return this.axios
      .get<GetSourceResponse>(
        `${this.apiBase}/${this.service}/v2/admin/source_medias/media_pivots/${mediaId}`,
      )
      .then((response) => response.data);
  }

  async getEncodingProfileList(): Promise<GetEncodingProfileListResponse> {
    return this.axios
      .get<GetEncodingProfileListResponse>(
        `${this.routeBase}/admin/encoding_profiles`,
      )
      .then((response) => response.data);
  }

  async setEncodingProfileDefault(
    uuid: string,
  ): Promise<SetEncodingProfileDefaultResponse> {
    return this.axios
      .put<SetEncodingProfileDefaultResponse>(
        `${this.routeBase}/admin/encoding_profiles/${uuid}/set_default`,
      )
      .then((response) => response.data);
  }

  async deleteEncodingProfile(uuid: string): Promise<unknown> {
    return this.axios
      .delete(`${this.routeBase}/admin/encoding_profiles/${uuid}`)
      .then((response) => response.data);
  }

  async getEncodingProfile(uuid: string): Promise<GetEncodingProfileResponse> {
    return this.axios
      .get<GetEncodingProfileResponse>(
        `${this.routeBase}/admin/encoding_profiles/${uuid}`,
      )
      .then((response) => response.data);
  }

  async getQualitiesCost(): Promise<GetQualitiesCostResponse> {
    return this.axios
      .get<GetQualitiesCostResponse>(
        `${this.routeBase}/admin/encoding_profiles/qualities_cost`,
      )
      .then((response) => response.data);
  }

  async updateEncodingProfile({
    uuid,
    data,
  }: UpdateEncodingProfileParams): Promise<UpdateEncodingProfileResponse> {
    return this.axios
      .put<UpdateEncodingProfileResponse>(
        `${this.routeBase}/admin/encoding_profiles/${uuid}`,
        data,
      )
      .then((response) => response.data);
  }

  async createEncodingProfile(
    data: EncodingProfilePayload,
  ): Promise<CreateEncodingProfileResponse> {
    return this.axios
      .post<CreateEncodingProfileResponse>(
        `${this.routeBase}/admin/encoding_profiles`,
        data,
      )
      .then((response) => response.data);
  }

  async deleteStream(streamId: string): Promise<GetStreamsResponse> {
    if (!streamId) return Promise.reject(new Error("streamId is required"));

    return this.axios
      .delete<GetStreamsResponse>(
        `${this.apiBase}/${this.service}/v2/admin/hosted_streams/${streamId}`,
      )
      .then((response) => response.data);
  }

  async updateStream(
    streamId: string,
    data: UpdateStreamData,
  ): Promise<GetStreamsResponse> {
    if (!data.name && !data.language_uuid && !data.type)
      return Promise.reject(
        new Error("name, language_uuid, or type is required"),
      );

    return this.axios
      .put<GetStreamsResponse>(
        `${this.apiBase}/${this.service}/v2/admin/hosted_streams/${streamId}`,
        data,
      )
      .then((response) => response.data);
  }

  async createS3Upload(
    data: CreateS3UploadPayload,
  ): Promise<CreateS3UploadResponse> {
    return this.axios
      .post<CreateS3UploadResponse>(
        `${this.apiBase}/${this.service}/v2/admin/source_medias/upload/initiate`,
        data,
      )
      .then((response) => response.data);
  }

  async uploadStart(data: UploadStartPayload): Promise<UploadStartResponse> {
    return this.axios
      .post<UploadStartResponse>(
        `${this.apiBase}/${this.service}/v2/admin/source_medias/upload/start`,
        data,
      )
      .then((response) => response.data);
  }

  async uploadVtt({
    source_media_uuid,
    clean_media_name,
    media_uuid,
    file,
  }: UploadVttPayload): Promise<UploadVttResponse> {
    const formData = new FormData();
    formData.append("content", file);
    formData.append("source_media_uuid", source_media_uuid);
    formData.append("media_name", clean_media_name);
    formData.append("media_uuid", media_uuid);
    formData.append("type", "subtitles");
    return this.axios
      .post<UploadVttResponse>(
        `${this.apiBase}/${this.service}/v2/admin/source_medias/upload`,
        formData,
      )
      .then((response) => response.data);
  }

  async getEncodingPlan(): Promise<GetEncodingPlanResponse> {
    return this.axios
      .get<GetEncodingPlanResponse>(
        `${this.apiBase}/${this.service}/v2/admin/encoding_plan`,
      )
      .then((response) => response.data);
  }
}

const EncoderAPI = new EncoderAPIClass({
  token: localStorage.token,
});

export default EncoderAPI;
